import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("rp_missing_token"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Mirror backend policy (12 chars, upper, lower, digit, symbol)
    if (pwd.length < 12) return setError(t("rp_min_12"));
    if (!/[a-z]/.test(pwd)) return setError(t("rp_need_lower"));
    if (!/[A-Z]/.test(pwd)) return setError(t("rp_need_upper"));
    if (!/[0-9]/.test(pwd)) return setError(t("rp_need_digit"));
    if (!/[^A-Za-z0-9]/.test(pwd)) return setError(t("rp_need_symbol"));
    if (pwd !== pwd2) return setError(t("rp_mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success(t("rp_success_toast"));
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) setError(t("rp_link_expired"));
      else if (status === 404) setError(t("rp_link_used_or_invalid"));
      else setError(err?.response?.data?.detail || t("rp_update_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen-safe bg-obsidian text-white flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="glass-card rounded-md p-8 sm:p-10 max-w-md w-full">
        <div className="mb-6 flex justify-center"><Logo /></div>
        {done ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display text-3xl text-gold-light text-center mb-2">{t("rp_done_title")}</h1>
            <p className="text-muted text-sm text-center mb-6">{t("rp_done_body")}</p>
          </>
        ) : error && !pwd ? (
          <>
            <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h1 className="font-display text-3xl text-gold-light text-center mb-2">{t("rp_invalid_link_title")}</h1>
            <p className="text-muted text-sm text-center mb-6">{error}</p>
            <Link to="/forgot-password" className="btn-gold w-full justify-center">{t("rp_request_new_link")}</Link>
          </>
        ) : (
          <form onSubmit={submit}>
            <h1 className="font-display text-3xl text-gold-light mb-2">{t("rp_new_password_h1")}</h1>
            <p className="text-muted text-sm mb-6">{t("rp_hint")}</p>

            <label className="font-label text-faded block mb-2">{t("rp_new_password_label")}</label>
            <div className="relative mb-4">
              <Lock className="w-4 h-4 text-faded absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                required
                minLength={8}
                className="input-field pl-10"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                data-testid="reset-pwd"
                autoFocus
              />
            </div>

            <label className="font-label text-faded block mb-2">{t("rp_confirm_label")}</label>
            <div className="relative mb-4">
              <Lock className="w-4 h-4 text-faded absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                required
                minLength={8}
                className="input-field pl-10"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                data-testid="reset-pwd2"
              />
            </div>

            {error && (
              <div className="text-red-400 text-xs mb-4">{error}</div>
            )}

            <button type="submit" disabled={loading} className="btn-gold w-full justify-center" data-testid="reset-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? t("rp_updating") : t("rp_update_cta")}
            </button>
          </form>
        )}
        <p className="text-faded text-xs text-center mt-6">
          {t("fp_need_help")} <a href="mailto:support@keepsoul.app" className="text-gold-light hover:underline">support@keepsoul.app</a>
        </p>
      </div>
    </div>
  );
}
